import { Button, Group, Stack, Textarea } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';

import { CommentListItem } from '@e-origin/contracts';

import { useCreateComment, useUpdateComment } from '@hooks/comment';

interface EditCommentProps {
  entityId: string;
  parentEntityId?: string;
  comment?: CommentListItem;
  onHide: () => void;
}

const commentSchema = z.object({
  text: z.string().min(1, { message: 'Required' }),
});

export const EditComment: React.FC<EditCommentProps> = ({ entityId, parentEntityId, comment, onHide }) => {
  const createCommentMutation = useCreateComment();
  const updateCommentMutation = useUpdateComment();

  const form = useForm({
    initialValues: {
      text: comment?.text ?? '',
    },
    validate: zodResolver(commentSchema),
    validateInputOnBlur: true,
  });

  const isEdit = Boolean(comment?._id);
  const isPending = createCommentMutation.isPending || updateCommentMutation.isPending;

  const handleSubmit = async (values: typeof form.values) => {
    if (isEdit) {
      await updateCommentMutation.mutateAsync({
        commentId: comment._id,
        text: values.text,
      });
    } else {
      await createCommentMutation.mutateAsync({
        text: values.text,
        entityId,
        ...(parentEntityId ? { entityParentId: parentEntityId } : {}),
      });
    }

    onHide();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm">
        <Textarea {...form.getInputProps('text')} autosize minRows={3} maxRows={6} data-pw="comment-textarea" />

        <Group justify="center" mt="sm">
          <Button type="button" onClick={onHide} disabled={isPending} variant="transparent" size="sm">
            Cancel
          </Button>
          <Button type="submit" loading={isPending} disabled={!form.isValid()} size="sm">
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
